import { useEffect, useState } from 'react';
import { api, ApiError } from '../../api/client.js';
import { useToast } from '../../context/ToastContext.jsx';
import LoadingState from '../LoadingState.jsx';
import EmptyState from '../EmptyState.jsx';
import Modal from '../Modal.jsx';
import KeyValueEditor from '../KeyValueEditor.jsx';

/**
 * Client-side Prebid.js bidder management — migrated from packages/dashboard's BiddersTab.jsx
 * (packages/dashboard/src/components/BiddersTab.jsx) as part of the dashboard consolidation phase.
 *
 * This manages an entirely separate mechanism from the Demand Partners tab: `bidders` /
 * `publisher_bidders` / `placement_bidder_params` are the ~8 third-party Prebid.js bidders that bid
 * directly in the browser and never touch auction-service's runAuction.js or the demand_partners/
 * margin_pct mechanism at all (see docs/BIDDIGI_PLATFORM_ARCHITECTURE.md section 3). Reuses the
 * existing admin API unchanged — GET /api/publishers/:id/bidders, PUT /api/publishers/:id/bidders/:code
 * — in both the Express and Cloudflare Worker entry points; no backend or data-model change was made
 * for this migration.
 */
export default function BiddersTab({ publisherId }) {
  const toast = useToast();
  const [bidders, setBidders] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [error, setError] = useState(null);
  const [savingCode, setSavingCode] = useState(null);
  const [editingParamsFor, setEditingParamsFor] = useState(null);

  async function load() {
    setError(null);
    try {
      const rows = await api.listPublisherBidders(publisherId);
      setBidders(rows);
      setDrafts(Object.fromEntries(rows.map((b) => [b.code, b.params || {}])));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load bidders.');
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [publisherId]);

  async function toggle(bidder) {
    setSavingCode(bidder.code);
    try {
      await api.putPublisherBidder(publisherId, bidder.code, {
        enabled: !bidder.enabled,
        params: drafts[bidder.code] ?? bidder.params ?? {},
      });
      toast.success(`${bidder.name} ${!bidder.enabled ? 'enabled' : 'disabled'} for this publisher.`);
      await load();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not update bidder.');
    } finally {
      setSavingCode(null);
    }
  }

  if (error) return <EmptyState title="Couldn't load bidders" message={error} />;
  if (bidders === null) return <LoadingState label="Loading bidders" />;
  if (bidders.length === 0) {
    return (
      <EmptyState
        title="No bidders in the catalog"
        message="The client-side Prebid.js bidder catalog (packages/config-backend's bidders table) is empty."
      />
    );
  }

  return (
    <section className="card card--flush">
      <div className="card__header card__header--padded">
        <h2 className="card__title">Client-side Prebid.js bidders</h2>
        <span className="card__hint">
          Toggle which of the {bidders.length} bidders compiled into the wrapper bundle are enabled for this
          publisher, and set each one's per-publisher params. These bid directly in the browser and never touch
          the server-side auction — see Demand Partners for BidDigi's own server-side connectors.
        </span>
      </div>
      <div className="table-scroll">
        <table className="data-table">
          <thead>
            <tr>
              <th>Bidder</th>
              <th>Media types</th>
              <th>Status</th>
              <th>Params</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {bidders.map((b) => (
              <tr key={b.code}>
                <td>
                  <div className="cell-title">{b.name}</div>
                  <div className="cell-subtitle">{b.code}</div>
                </td>
                <td className="cell-muted">{(b.media_types || []).join(', ') || '—'}</td>
                <td>
                  <label className="switch">
                    <input
                      type="checkbox"
                      checked={!!b.enabled}
                      disabled={savingCode === b.code}
                      onChange={() => toggle(b)}
                    />
                    <span className="switch__track">
                      <span className="switch__thumb" />
                    </span>
                  </label>
                </td>
                <td className="cell-muted">{Object.keys(b.params || {}).length} param(s)</td>
                <td>
                  <button className="btn btn--ghost btn--sm" onClick={() => setEditingParamsFor(b)}>
                    Edit params
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editingParamsFor && (
        <BidderParamsModal
          title={`${editingParamsFor.name} — publisher params`}
          initial={drafts[editingParamsFor.code] || editingParamsFor.params || {}}
          onClose={() => setEditingParamsFor(null)}
          onSave={async (params) => {
            await api.putPublisherBidder(publisherId, editingParamsFor.code, {
              enabled: editingParamsFor.enabled,
              params,
            });
            toast.success('Params updated.');
            setEditingParamsFor(null);
            load();
          }}
        />
      )}
    </section>
  );
}

function BidderParamsModal({ title, initial, onClose, onSave }) {
  const toast = useToast();
  const [params, setParams] = useState(initial);
  const [saving, setSaving] = useState(false);

  async function handleSave() {
    setSaving(true);
    try {
      await onSave(params);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not save params.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      title={title}
      onClose={onClose}
      footer={
        <>
          <button className="btn btn--ghost" onClick={onClose}>
            Cancel
          </button>
          <button className="btn btn--primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      <KeyValueEditor value={params} onChange={setParams} disabled={saving} />
    </Modal>
  );
}
